import React from "react";
import { ArrowRight, CheckCircle2, Wallet, Coins, Trophy } from "lucide-react";
import { PROFIT_PARTS } from "../mock/data";
import { Wrap, SectionHead, Note } from "./Section";
import { useReveal } from "../hooks/useReveal";

const FLOW = [
  { n: "01", title: "You introduce someone", body: "You pass on your personal link to the webinar. They see the same session you did — the same track record, the same account structures, the same risks — before deciding anything." },
  { n: "02", title: "They trade their own account", body: "If they continue, they open a TAG Markets account in their own name and fund it themselves. You never hold their money, never manage it and never advise on it." },
  { n: "03", title: "Profit is made — and shared", body: "When their account closes profit, the performance fee is charged on that profit. A quarter of it flows back through the community structure, and your level's share is paid to you." },
];

const SPLIT_1000 = [
  ["Client keeps (70%)", "$700"], ["Traders (5%)", "$50"],
  ["Community pool (25%)", "$250"], ["One level of that pool (2.5%)", "$25"],
];

const PROPS = [
  { icon: Wallet, title: "Paid from the performance fee", body: "Your share comes out of the 30% already charged on realised profit. It does not increase anyone's cost and it is never deducted from their capital." },
  { icon: CheckCircle2, title: "Only on realised profit", body: "The fee — and therefore the profit share — exists only when profit is actually closed and above the account's previous high-water mark." },
  { icon: Coins, title: "Aligned by design", body: "The profit-share part of the plan pays when the accounts you introduced earn, and only then. If they make nothing, you make nothing from it." },
  { icon: Trophy, title: "Tracked in CopyX", body: "Commissions, qualification status and incentive progress are all visible in CopyX under My Commissions, so what you are owed is not something you have to take on trust." },
];

export default function ProfitSharing() {
  const [ref, inView] = useReveal();
  return (
    <Wrap id="profit">
      <SectionHead tag="Profit sharing" title="You can share in the profit" highlight="you help create"
        desc="Following the strategy yourself is the product. Sharing it is optional — you never have to. If you do, the AiTech compensation plan pays you out of the same 30% performance fee the strategy already charges on realised profit, not out of anyone's deposit and not as an extra cost to the person you introduced." />

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {FLOW.map((f, i) => (
          <div key={i} className="card rounded-2xl p-7">
            <div className="mono text-accent text-sm mb-4">{f.n}</div>
            <h3 className="display text-base mb-3">{f.title}</h3>
            <p className="text-[13px] text-[var(--muted)] leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>

      {/* Split visualization */}
      <div ref={ref} className={`reveal ${inView ? "in" : ""} mt-6 card rounded-2xl p-7`}>
        <div className="label text-[var(--muted)] mb-5">How realised profit is split</div>
        <div className="flex h-14 rounded-lg overflow-hidden mb-3">
          <div className="bg-white/[0.06] flex items-center justify-center" style={{ width: "70%" }}>
            <span className="mono text-[11px] text-[var(--text)]">70% CLIENT</span>
          </div>
          <div className="bg-accent/40 flex items-center justify-center" style={{ width: "5%" }} />
          <div className="bg-accent flex items-center justify-center" style={{ width: "25%" }}>
            <span className="mono text-[11px] text-[#0a0d08]">25% COMMUNITY</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-[12px] text-[var(--muted)]">
          <div><span className="text-[var(--text)] font-medium">70% Client</span> — stays with the account that made the profit</div>
          <div><span className="text-[var(--text)] font-medium">5% Traders</span> — the desk running the strategy</div>
          <div><span className="text-[var(--text)] font-medium">25% Community</span> — distributed across 10 levels</div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/[0.07]">
          <div className="label text-[var(--muted)] mb-4">The community portion, across 10 levels</div>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="border border-accent/20 bg-accent/[0.04] rounded-lg py-3 text-center">
                <div className="mono text-accent text-xs">L{i + 1}</div>
                <div className="text-[9px] text-[var(--muted)] mt-1">2.5%</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[var(--muted)] mt-3">The 25% community portion is split into ten equal levels of 2.5% of trading profit each — level 1 is the people you introduced directly, levels 2 to 10 are the people they go on to introduce.</p>
        </div>
      </div>

      {/* On $1000 */}
      <div className="mt-6 card rounded-2xl p-7">
        <div className="label text-[var(--muted)] mb-5">On $1,000 of realised trading profit</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SPLIT_1000.map((s, i) => (
            <div key={i} className="border border-white/[0.06] rounded-lg p-4">
              <div className="text-[11px] text-[var(--muted)] mb-2 leading-tight">{s[0]}</div>
              <div className={`display text-xl ${i === 0 ? "text-[var(--text)]" : "text-accent"}`}>{s[1]}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PROPS.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={i} className="card rounded-2xl p-6">
              <Icon size={20} className="text-accent mb-4" strokeWidth={1.6} />
              <h3 className="text-[14px] font-semibold text-[var(--text)] mb-2">{p.title}</h3>
              <p className="text-[12px] text-[var(--muted)] leading-relaxed">{p.body}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-10">
        <h3 className="display text-xl mb-2">Three further parts of the plan</h3>
        <p className="text-[13px] text-[var(--muted)] max-w-3xl mb-6">Profit sharing is the part tied directly to results. The published plan also contains three components that are tied to activity rather than profit — worth understanding precisely because they work differently.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {PROFIT_PARTS.map((p, i) => (
            <div key={i} className="card rounded-2xl p-7">
              <h4 className="text-[15px] font-semibold text-[var(--text)] mb-3">{p.title}</h4>
              <p className="text-[13px] text-[var(--muted)] leading-relaxed mb-4">{p.body}</p>
              <p className="text-[12px] text-accent/90 leading-relaxed border-t border-white/[0.06] pt-3">{p.note}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 card rounded-2xl p-7 max-w-3xl">
        <div className="label text-accent mb-3">What it is not</div>
        <ul className="space-y-2 text-[13px] text-[var(--muted)] leading-relaxed">
          <li>• Not required. The social trading service works exactly the same whether you ever introduce anyone or not.</li>
          <li>• Not a fee added on top of what the person you introduced pays — their cost is the same 30% of realised profit either way.</li>
          <li>• Not a promise of income. Profit sharing pays nothing in a month where no profit is realised.</li>
          <li>• Not a way around trading risk. Everything here depends on real accounts trading real capital, and those accounts can lose money.</li>
        </ul>
        <a href="#join" className="mt-5 inline-flex items-center gap-2 label text-accent hover:gap-3 transition-all">
          Ask about profit sharing <ArrowRight size={13} />
        </a>
      </div>

      <Note>
        Commission rates, levels and qualification rules are set by the platform and can change — always check the current official compensation plan and your status in CopyX. Introducing others may be a regulated activity where you live: check your local rules and never give investment advice.
      </Note>
    </Wrap>
  );
}
